/**
 * Dopravní info v navigaci rozvozce (2026-10-23) — výpočty bez obrazovky.
 *
 * Vstup: balíček z OS (silnice s třídou a limitem, stopky, přednosti,
 * radary, úsekové měření), hlášení řidičů, poloha z GPS a trasa před
 * řidičem (`routeGeometry` z useRouteGeometry — začíná v jeho poloze).
 *
 * Silnice se hledají přes mřížku 100 m, takže výpočet stihne každý bod GPS.
 * Stopka a přednost platí, jen když leží na téže silnici, po které vede
 * trasa — jinak by hlásila stopky vedlejších ulic při jízdě po hlavní.
 */

const BUNKA = 100;          // m — mřížka pro hledání silnic
const NA_SILNICI = 22;      // m — dál od silnice už limit neplatí
const HLAVNI = 4;           // třídy 0–4 (dálnice … silnice III. třídy) jsou hlavní

/** Připraví balíček: lokální souřadnice v metrech a mřížku úseků. */
export function pripravBalicek(data) {
    if (!data?.cesty?.length) return null;
    const [lat0, lng0] = data.stred;
    const kx = 111320 * Math.cos((lat0 * Math.PI) / 180);
    const ky = 110540;
    const xy = (lat, lng) => [(lng - lng0) * kx, (lat - lat0) * ky];

    const naBody = (plocha) => {
        const body = [];
        for (let i = 0; i < plocha.length; i += 2) body.push(xy(plocha[i], plocha[i + 1]));
        return body;
    };
    const cesty = data.cesty.map(([trida, limit, odvozeny, plocha, nezpevnena]) => (
        { trida, limit, odvozeny: !!odvozeny, nezpevnena: !!nezpevnena, body: naBody(plocha) }
    ));
    // Polní, lesní a pěší cesty (2026-10-26) — vlastní mřížka, jen pro
    // přerušovanou linku trasy; limity a značky dál jen ze silnic.
    const vedlejsi = (data.vedlejsi ?? []).map(([typ, plocha]) => ({ typ, body: naBody(plocha) }));

    const b = { xy, cesty, mrizka: mrizkaUseku(cesty), vedlejsi, mrizkaVedlejsi: mrizkaUseku(vedlejsi) };
    const body = (seznam, typ) => (seznam ?? []).map(([lat, lng], i) => {
        const p = xy(lat, lng);
        return { id: `${typ}${i}`, typ, lat, lng, p, cesta: nejblizsiCesta(b, p, null, 15)?.ci ?? null };
    });
    b.stopky = body(data.stopky, 'stopka');
    b.prednost = body(data.prednost, 'prednost');
    b.radary = body(data.radary, 'radar');
    b.useky = (data.useky ?? []).map(([la1, lo1, la2, lo2], i) => ({
        id: `usek${i}`, typ: 'usek', lat: la1, lng: lo1, p: xy(la1, lo1), konec: xy(la2, lo2),
    }));
    return b;
}

/** Mřížka 100 m: buňka → [index cesty, index úseku]. */
function mrizkaUseku(cesty) {
    const mrizka = new Map();
    const pridej = (k, ref) => { const s = mrizka.get(k); s ? s.push(ref) : mrizka.set(k, [ref]); };
    cesty.forEach((c, ci) => {
        for (let i = 0; i < c.body.length - 1; i++) {
            const [ax, ay] = c.body[i], [bx, by] = c.body[i + 1];
            const x1 = Math.floor(Math.min(ax, bx) / BUNKA), x2 = Math.floor(Math.max(ax, bx) / BUNKA);
            const y1 = Math.floor(Math.min(ay, by) / BUNKA), y2 = Math.floor(Math.max(ay, by) / BUNKA);
            for (let x = x1; x <= x2; x++) for (let y = y1; y <= y2; y++) pridej(`${x}:${y}`, [ci, i]);
        }
    });
    return mrizka;
}

function vzdUsecka(p, a, b) {
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
    return { d: Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy)), smer: Math.atan2(dy, dx) };
}

/**
 * Nejbližší silnice k bodu. Se směrem jízdy (`smerRad`, matematický úhel)
 * dá přednost silnici, která vede stejným směrem — na křižovatce jinak
 * vyhrává příčná ulice.
 */
export function nejblizsiCesta(b, p, smerRad = null, max = NA_SILNICI, vedlejsi = false) {
    const cx = Math.floor(p[0] / BUNKA), cy = Math.floor(p[1] / BUNKA);
    const mrizka = vedlejsi ? b.mrizkaVedlejsi : b.mrizka;
    const seznam = vedlejsi ? b.vedlejsi : b.cesty;
    let nej = null;
    for (let x = cx - 1; x <= cx + 1; x++) for (let y = cy - 1; y <= cy + 1; y++) {
        for (const [ci, i] of mrizka.get(`${x}:${y}`) ?? []) {
            const c = seznam[ci];
            const { d, smer } = vzdUsecka(p, c.body[i], c.body[i + 1]);
            if (d > max) continue;
            const odklon = smerRad == null ? 0 : 1 - Math.abs(Math.cos(smer - smerRad));
            const skore = d + odklon * 18;
            if (!nej || skore < nej.skore) nej = { ci, d, skore, cesta: c };
        }
    }
    return nej;
}

/**
 * Po čem trasa vede — typ pro každý úsek geometrie ([lat,lng]):
 * 'auto' | 'nezpevnena' | 'pesi'. Úsek patří vedlejší cestě, jen když
 * je k ní znatelně blíž než k silnici (chodník podél silnice nevyhraje).
 * `pesiOdKonceM` = posledních N metrů je pěší dojezd ze serveru.
 */
export function typyUseku(b, geometry, pesiOdKonceM = 0) {
    const n = Math.max(0, geometry.length - 1);
    const typy = new Array(n).fill('auto');
    if (n === 0) return typy;

    if (b) {
        for (let i = 0; i < n; i++) {
            const a = b.xy(geometry[i][0], geometry[i][1]);
            const c = b.xy(geometry[i + 1][0], geometry[i + 1][1]);
            const p = [(a[0] + c[0]) / 2, (a[1] + c[1]) / 2];
            const smer = Math.atan2(c[1] - a[1], c[0] - a[0]);
            const silnice = nejblizsiCesta(b, p, smer, 15);
            const vedlejsi = b.vedlejsi?.length ? nejblizsiCesta(b, p, smer, 12, true) : null;
            if (vedlejsi && (!silnice || vedlejsi.d + 4 < silnice.d)) {
                typy[i] = vedlejsi.cesta.typ === 0 ? 'nezpevnena' : 'pesi';
            } else if (silnice?.cesta.nezpevnena) {
                typy[i] = 'nezpevnena';
            }
        }
    }

    if (pesiOdKonceM > 0) {
        let odKonce = 0;
        for (let i = n - 1; i >= 0 && odKonce < pesiOdKonceM - 1; i--) {
            typy[i] = 'pesi';
            odKonce += Math.hypot(
                (geometry[i + 1][1] - geometry[i][1]) * 111320 * Math.cos((geometry[i][0] * Math.PI) / 180),
                (geometry[i + 1][0] - geometry[i][0]) * 110540,
            );
        }
    }
    return typy;
}

/** Trasa před řidičem v metrech: body s ujetou vzdáleností. */
export function trasaVpred(b, geometry, maxM = 1000) {
    const out = [];
    let ujeto = 0;
    for (let i = 0; i < geometry.length; i++) {
        const p = b.xy(geometry[i][0], geometry[i][1]);
        if (i > 0) ujeto += Math.hypot(p[0] - out[i - 1].p[0], p[1] - out[i - 1].p[1]);
        out.push({ p, s: ujeto });
        if (ujeto > maxM) break;
    }
    return out;
}

/** Kde na trase je bod nejblíž: { s (m od řidiče), d (m od trasy), smer }. */
function naTrase(trasa, p) {
    let nej = null;
    for (let i = 0; i < trasa.length - 1; i++) {
        const a = trasa[i].p, c = trasa[i + 1].p;
        const dx = c[0] - a[0], dy = c[1] - a[1];
        const l2 = dx * dx + dy * dy;
        const t = l2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
        const d = Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
        if (!nej || d < nej.d) nej = { d, s: trasa[i].s + Math.sqrt(l2) * t, smer: Math.atan2(dy, dx) };
    }
    return nej;
}

/** Bod na trase ve vzdálenosti s. */
function bodNaTrase(trasa, s) {
    for (let i = 0; i < trasa.length - 1; i++) {
        if (trasa[i + 1].s >= s) {
            const k = (s - trasa[i].s) / ((trasa[i + 1].s - trasa[i].s) || 1);
            const a = trasa[i].p, c = trasa[i + 1].p;
            return { p: [a[0] + (c[0] - a[0]) * k, a[1] + (c[1] - a[1]) * k], smer: Math.atan2(c[1] - a[1], c[0] - a[0]) };
        }
    }
    return null;
}

/**
 * Co čeká řidiče na trase. Vrací seznam událostí seřazený podle vzdálenosti:
 * { id, typ: stopka|prednost|hlavni|radar|usek|hlidka|nehoda|prekazka, s, lat?, lng? }
 */
export function udalostiNaTrase(b, trasa, hlaseni = [], nastaveni = {}) {
    if (!b || trasa.length < 2) return [];
    const konec = trasa[trasa.length - 1].s;
    const out = [];

    // Silnice, po které trasa vede v daném bodě (podle směru trasy).
    const cestaV = (s) => {
        const bod = bodNaTrase(trasa, s);
        return bod ? nejblizsiCesta(b, bod.p, bod.smer, 15) : null;
    };

    if (nastaveni.stopky !== false) {
        for (const z of [...b.stopky, ...b.prednost]) {
            if (Math.hypot(z.p[0] - trasa[0].p[0], z.p[1] - trasa[0].p[1]) > konec + 30) continue;
            const t = naTrase(trasa, z.p);
            if (!t || t.d > 18 || t.s < 5) continue;
            // Značka patří silnici, po které trasa přijíždí — ne příčné.
            const pred = cestaV(Math.max(0, t.s - 20));
            if (z.cesta != null && pred && pred.ci !== z.cesta) continue;
            out.push({ id: z.id, typ: z.typ, s: t.s, lat: z.lat, lng: z.lng });
        }

        // Najetí z vedlejší silnice na hlavní (pokryje i značky, které v mapě chybí).
        const ted = cestaV(10);
        if (ted && ted.cesta.trida > HLAVNI) {
            for (let s = 20; s <= Math.min(konec, 400); s += 10) {
                const c = cestaV(s);
                if (c && c.cesta.trida <= HLAVNI) {
                    const blizkaZnacka = out.some((u) => Math.abs(u.s - s) < 35);
                    if (!blizkaZnacka) out.push({ id: `hlavni${c.ci}`, typ: 'hlavni', s: Math.max(0, s - 10) });
                    break;
                }
                if (c && c.ci !== ted.ci && c.cesta.trida > HLAVNI) break; // odbočil do jiné vedlejší
            }
        }
    }

    if (nastaveni.radary !== false) {
        for (const r of b.radary) {
            const t = naTrase(trasa, r.p);
            if (t && t.d < 25 && t.s > 0) out.push({ id: r.id, typ: 'radar', s: t.s, lat: r.lat, lng: r.lng });
        }
        for (const u of b.useky) {
            const t = naTrase(trasa, u.p);
            if (t && t.d < 30 && t.s > 0) out.push({ id: u.id, typ: 'usek', s: t.s, lat: u.lat, lng: u.lng });
        }
    }

    if (nastaveni.hlaseni !== false) {
        for (const h of hlaseni) {
            const p = b.xy(h.lat, h.lng);
            const t = naTrase(trasa, p);
            if (t && t.d < 40 && t.s > 0) out.push({ id: `h${h.id}`, typ: h.typ, s: t.s, lat: h.lat, lng: h.lng, kdo: h.kdo, vytvoreno: h.vytvoreno });
        }
    }

    return out.sort((a, c) => a.s - c.s);
}

/** Limit na silnici, po které řidič jede. */
export function limitV(b, lat, lng, headingDeg = null) {
    if (!b) return null;
    // GPS heading: stupně od severu po směru hodin → matematický úhel
    const smer = headingDeg == null || Number.isNaN(headingDeg) ? null : ((90 - headingDeg) * Math.PI) / 180;
    const c = nejblizsiCesta(b, b.xy(lat, lng), smer);
    return c ? { limit: c.cesta.limit, odvozeny: c.cesta.odvozeny, trida: c.cesta.trida } : null;
}

/** Od jaké vzdálenosti se událost ukazuje a kdy se hlásí hlasem (m). */
export const DOSAH = {
    stopka:   { ukazat: 250, hlas: 100 },
    prednost: { ukazat: 250, hlas: 100 },
    hlavni:   { ukazat: 200, hlas: 90 },
    radar:    { ukazat: 700, hlas: 500 },
    usek:     { ukazat: 900, hlas: 800 },
    hlidka:   { ukazat: 600, hlas: 300 },
    nehoda:   { ukazat: 600, hlas: 300 },
    prekazka: { ukazat: 400, hlas: 200 },
};

/** Přednost při souběhu hlasu: menší = důležitější. */
export const PRIORITA = { stopka: 0, prednost: 1, hlavni: 1, nehoda: 2, prekazka: 2, radar: 3, usek: 3, rychlost: 4, hlidka: 5 };
